import { useState } from 'react';
import {
  Button,
  Image,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuList,
  MenuListProps,
  useToast,
} from '@chakra-ui/react';
import { useDisconnect } from 'wagmi';
import Jazzicon from 'react-jazzicon/dist/Jazzicon';
import { jsNumberForAddress } from 'react-jazzicon';

import { CustomMenuItem, DownIcon } from './CustomMenuItem';
import copyImg from '../assets/copy.svg';
import logoutImg from '../assets/disconnect.svg';
import walletIcon from '../assets/wallet.svg';
import { useIsMobile } from '../hooks/useMobile';

const WalletDropDown = ({
  isConnected,
  account,
  openConnectModal,
  ...rest
}: {
  isConnected: boolean;
  account?: {
    address: string;
    balanceDecimals?: number | undefined;
    balanceFormatted?: string | undefined;
    balanceSymbol?: string | undefined;
    displayBalance?: string | undefined;
    displayName: string;
    ensAvatar?: string | undefined;
    ensName?: string | undefined;
    hasPendingTransactions: boolean;
  };
  openConnectModal: () => void;
} & MenuListProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();
  const { disconnect } = useDisconnect();

  if (!isConnected) {
    return <CustomConnectWalletButton openConnectModal={openConnectModal} />;
  }
  return (
    <Menu
      autoSelect={false}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    >
      <MenuButton
        as={Button}
        color={isOpen ? 'brand' : 'c-103'}
        variant={'outline'}
        rightIcon={<DownIcon transform={isOpen ? 'rotateX(180deg)' : 'none'} />}
        leftIcon={
          <Jazzicon
            paperStyles={{ width: '2rem', height: '2rem' }}
            svgStyles={{ width: '2rem', height: '2rem' }}
            diameter={28}
            seed={jsNumberForAddress(account?.address || '')}
          />
        }
        {...(rest as MenuButtonProps)}
      >
        {account?.displayName}
      </MenuButton>
      <MenuList
        color="c-103"
        zIndex={101}
        fontWeight={'medium'}
        bgColor={'c-106'}
        border="none"
        p={2}
        borderRadius={'6px'}
        minWidth="0"
        fontSize="md"
        mt={'-6px'}
        {...rest}
      >
        <CustomMenuItem
          icon={<Image src={copyImg} w={6} h={6} />}
          onClick={() => {
            navigator.clipboard.writeText(account?.address || '');
            toast({
              title: `Address copied to your clipboard`,
              status: 'success',
            });
          }}
        >
          Copy Address
        </CustomMenuItem>
        <CustomMenuItem
          icon={<Image src={logoutImg} w={6} h={6} />}
          onClick={() => {
            disconnect();
          }}
        >
          Disconnect
        </CustomMenuItem>
      </MenuList>
    </Menu>
  );
};

export const CustomConnectWalletButton = ({
  hasImage = true,
  text = 'Connect Wallet',
  openConnectModal,
}: {
  hasImage?: boolean;
  text?: string;
  openConnectModal: () => void;
}) => {
  const { isMobile } = useIsMobile();
  return (
    <Button
      variant={'normal'}
      leftIcon={hasImage ? <Image src={walletIcon} w={6} h={6} /> : undefined}
      fontSize={isMobile ? '1.2rem' : 'sm'}
      onClick={openConnectModal}
    >
      {text}
    </Button>
  );
};

export default WalletDropDown;
